// EdAI Prep page content. House rules apply: no em-dashes anywhere, no pass
// probability language, and honesty moments stay visible.
//
// The specimen question below is a real production item from the platform,
// sourced from a certified review authored by John C. Ferguson, MD, FACS.
// The option letters in the explanation match the order shown here.

export const specimenQuestion = {
  blockLabel: 'Practice',
  positionLabel: 'Question 1 of 6',
  tag: 'Delayed / precocious puberty',
  cognitiveLevel: 'recall',
  stem:
    'A 9-year-old girl is referred to a general paediatric outpatient clinic with concerns about short stature. She is on the 2nd centile for height, with no dysmorphic features and normal systemic examination. The clinician wishes to assess skeletal maturation using a plain radiograph of the left hand and wrist. Which of the following best describes the Greulich-Pyle method of skeletal age assessment?',
  options: [
    {
      key: 'A',
      text: 'Assigning individual scores to each bone of the hand and wrist, then summing the scores to derive a total skeletal maturity score',
    },
    {
      key: 'B',
      text: 'Selecting a single index bone of the wrist to represent overall skeletal maturity by comparing it to age-matched standards',
    },
    {
      key: 'C',
      text: 'Measuring cortical thickness of the second metacarpal and correlating this with population-based normative data',
    },
    {
      key: 'D',
      text: 'Comparing the radiograph against standard reference plates by atlas matching, evaluating all hand and wrist bones collectively',
    },
  ],
  correctKey: 'D',
  explanation:
    'The Greulich-Pyle (GP) method uses atlas matching: the clinician compares the patient\u2019s hand and wrist radiograph to a series of standard reference plates. Crucially, all hand and wrist bones are evaluated collectively rather than individually. The claim confirms this approach is highly efficient (average analysis time 0.79 \u00b1 0.14 minutes), has excellent intra- and inter-rater reliability (0.993 / 0.992), and is well suited to non-specialist clinical screening, making it appropriate for an initial assessment in a general paediatric clinic. Option A describes the Tanner-Whitehouse (TW) method, which assigns individual scores to specific bones before summing them; this is a common misconception that conflates the two leading skeletal age methods. Option B describes a single-bone index approach, which misrepresents the GP method; GP evaluates all hand and wrist bones collectively, not a single representative bone. Option C describes a radiogrammetric technique based on metacarpal cortical thickness, which is a distinct and less commonly used approach not related to GP atlas matching.',
  claimRef: 'source: 89183',
  review: {
    title: 'Comprehensive Clinical Evaluation and Management of Abnormal Stature in Pediatrics',
    sourcedFrom: 'John C. Ferguson, MD, FACS',
  },
};

// Set to false to hide prices on the exam cards without touching layout.
export const showPricing = true;

export const liveExams = [
  {
    id: 'usmle',
    name: 'USMLE',
    detail: 'Steps 1, 2 CK, and 3',
    jurisdiction: 'United States',
    price: '$299 / year',
    note: 'One subscription covers all three steps. NBME register per step, through independent-practice management stems for Step 3.',
  },
  {
    id: 'mccqe',
    name: 'MCCQE Part I',
    detail: 'Medical Council of Canada',
    jurisdiction: 'Canada',
    price: 'CA$419 / year',
    note: 'English and French question content, weighted to the MCC blueprint.',
  },
  {
    id: 'ukmla',
    name: 'UKMLA AKT',
    detail: 'UK Medical Licensing Assessment',
    jurisdiction: 'United Kingdom',
    price: '\u00a3229 / year',
    note: 'UK drug names, reference ranges, and referral thresholds, in their own lane.',
  },
  {
    id: 'amc',
    name: 'AMC CAT MCQ',
    detail: 'Australian Medical Council',
    jurisdiction: 'Australia',
    price: 'AU$429 / year',
    note: 'Community-practice reasoning, weighted to the AMC\u2019s published patient-group blueprint.',
  },
  {
    id: 'pres',
    name: 'PRES Level 2',
    detail: 'Irish Medical Council',
    jurisdiction: 'Ireland',
    price: '\u20ac269 / year',
    note: 'Including professionalism and ethics framed to the four domains of Good Medical Practice.',
  },
];

export const inDevelopmentExam = {
  id: 'enarm',
  name: 'ENARM',
  detail: 'Examen Nacional de Aspirantes a Residencias M\u00e9dicas',
  jurisdiction: 'Mexico',
  note: 'In active development. The Spanish-language clinical-case engine and the exam\u2019s official CIFRHS content weighting are built; it goes live once the remaining source content is in place.',
};

export const comingSoonSkills = ['NAC OSCE', 'PLAB 2', 'NZREX Clinical'];

export const studyModes = [
  {
    id: 'practice',
    name: 'Practice blocks',
    lead: 'Twenty questions, then your due flashcards.',
    text: 'Free-form practice on demand. Every question grades instantly with a full explanation and a provenance badge naming who wrote and who certified the source behind it. The block finishes with a batch of due flashcards seeded around the concepts you just practiced.',
    details: ['Single-best-answer items in your exam\u2019s register', 'The certified source review one click away, streamed as the actual PDF', 'Targeted drilling: arrive from a weak concept and the block aims at it'],
  },
  {
    id: 'cases',
    name: 'Clinical cases',
    lead: 'One coherent patient, several linked decisions.',
    text: 'For exams built on case testlets, a shared vignette carries multiple linked questions. Cases are checked for clinical coherence before you ever see them: a patient course a real physician would find implausible is rejected, not served.',
    details: ['Shared vignette with linked single-best-answer items', 'Per-item explanations after you submit the whole case', 'Exam-native axes, like ENARM\u2019s level-of-management category, revealed only after grading'],
  },
  {
    id: 'mock',
    name: 'Mock exams',
    lead: 'Full length, timed, under real conditions.',
    text: 'A sticky timer, a question navigator with mark-for-review, and auto-submit at zero. The report breaks your score down by content area and by cognitive level, names your weakest and strongest areas, and updates your study plan from the attempt.',
    details: ['One included attempt per subscription, more by credit', 'By-area and by-cognitive-level score breakdown', 'Item-by-item answer review after the report'],
  },
  {
    id: 'plan',
    name: 'The study plan',
    lead: 'Your test date, your hours, your weak concepts.',
    text: 'A wizard takes your test date, minutes per day, and days per week, optionally calibrates with a short adaptive quiz, and builds a day-by-day program. Guided sessions walk the exact mix the plan wants today, and the plan re-projects itself after every session.',
    details: ['Four methodologies: spaced-repetition first, qbank mastery, foundational curriculum, or high-yield cram', 'A coverage contract showing exactly how every topic was weighted, and why', 'Pace, streaks, focus-area drills, and calendar export'],
  },
];

export const alsoIncluded = [
  { name: 'Flashcards', text: 'FSRS scheduling that projects retention to your actual test date, with export to a real Anki .apkg file.' },
  { name: 'Study guides', text: 'Synthesized per topic from the certified corpus, with listen-aloud controls, offline download, and a sources list naming every review used. No source content, no guide: it says so rather than fabricating.' },
  { name: 'The review library', text: 'Browse and read every certified review behind the question bank, independent of any question.' },
  { name: 'Progress', text: 'Per-exam mastery by system, weakest first, with one-click drilling into weak areas.' },
];

export const integrityGates = [
  {
    name: 'Vignette coherence',
    text: 'A separate review pass, never the model that wrote the question, reads each multi-question case and asks whether a real physician would find the patient course clinically plausible. Incoherent cases are rejected or trimmed.',
  },
  {
    name: 'Grounding integrity',
    text: 'For every explanation of why an option is wrong, an independent judge verifies the cited certified claim actually establishes it. Explanations that merely sound authoritative do not pass.',
  },
  {
    name: 'Structural defensibility',
    text: 'If the certified evidence cannot cleanly support one single best answer, the item is pulled rather than shipped with a manufactured key.',
  },
];

export const integrityCoda =
  'Every gate fails safe: when the system is uncertain, it regenerates, trims, or withholds. And the live pool is re-audited continuously, not just checked once at generation time.';

export const honestyMoments = [
  {
    title: 'Preparation progress, never a pass probability',
    text: 'The readiness metric is blueprint coverage and measured retention, shown with a confidence band: an early "keep going" range first, an approximate figure when the evidence supports one, a precise one only when it is earned. Nobody can honestly promise you a pass probability, so we do not.',
  },
  {
    title: 'Guides that refuse to fabricate',
    text: 'Ask for a study guide on a topic with no certified source content behind it and the product tells you so. It never synthesizes a guide from nothing.',
  },
  {
    title: 'Drills that admit when they cannot drill',
    text: 'If a weak concept cannot be targeted, the app quietly gives you an ordinary block rather than falsely claiming to be drilling it.',
  },
  {
    title: 'Translations ship after human review, not before',
    text: 'French is live for MCCQE. A machine-drafted Spanish layer exists across the platform today and stays gated until it clears physician review. Verified, then released.',
  },
];

export const faq = [
  {
    q: 'Is this just a general-purpose AI writing questions?',
    a: 'No. The knowledge is certified by physicians into a separate corpus before generation ever runs, and every generated explanation is independently machine-checked against that certified source before it ships. A generic AI question bank has neither step.',
  },
  {
    q: 'How do I know the content is right for my exam and my country?',
    a: 'Content is retrieved from a jurisdiction-scoped slice of the corpus and weighted to your exam\u2019s own published specification: the AMC\u2019s patient-group weighting, Mexico\u2019s CIFRHS clinical-impact matrix, the MCC blueprint. That scoping is structural, not a label.',
  },
  {
    q: 'Why will it not tell me my probability of passing?',
    a: 'Because no product can honestly compute one. Prep shows preparation progress: how much of the blueprint you have covered and how well you are retaining it, with a visible confidence band. It is the honest version of the number everyone else rounds up.',
  },
  {
    q: 'Can I keep using Anki?',
    a: 'Yes. Your deck exports as a standard .apkg file at no extra cost. The in-app scheduler uses FSRS, the same family of algorithm behind modern Anki.',
  },
  {
    q: 'What does a subscription include?',
    a: 'Everything for that exam: practice blocks, clinical cases, one included mock exam attempt (more by credit), the study plan, flashcards, study guides, the review library, and progress tracking. Subscriptions are per exam, priced in that exam\u2019s market currency.',
  },
];

// Per-exam subpage content, /products/prep/:examId. Blueprint sections describe
// the official specification structurally and link to the regulator; no invented
// percentages. Prices mirror the cards above and respect showPricing.

export const sharedIncluded = [
  'Practice blocks: twenty questions, then your due flashcards, on demand',
  'One included full-length timed mock exam attempt, more by credit',
  'The adaptive study plan, with guided daily sessions and the coverage contract',
  'FSRS flashcards with export to a real Anki .apkg file',
  'Synthesized study guides with listen-aloud and offline download',
  'The certified review library, readable in full behind every question',
  'Per-system progress tracking, weakest areas first',
];

export const examPages = {
  usmle: {
    status: 'live',
    name: 'USMLE',
    fullName: 'United States Medical Licensing Examination',
    jurisdiction: 'United States',
    regulator: 'FSMB and NBME',
    tagline: 'One subscription. All three steps. Each in its own voice.',
    price: '$299 / year',
    languages: ['English'],
    overview: [
      'A USMLE subscription covers Steps 1, 2 CK, and 3 together, because the exams are one arc: the same medicine, tested at three depths. Prep keeps the three registers separate, so a Step 1 block reads like Step 1 and a Step 3 block reads like independent practice.',
      'Every item is generated from the certified corpus scoped to the USMLE, passes the integrity gates, and carries its provenance badge: the certified source review, openable mid-question.',
    ],
    registers: [
      { name: 'Step 1', text: 'Basic-science mechanism: the why beneath the presentation, in NBME register.' },
      { name: 'Step 2 CK', text: 'Clinical diagnosis and management: the next best step, at clerkship depth.' },
      { name: 'Step 3', text: 'Independent-practice management stems: the unsupervised decision, as the exam frames it.' },
    ],
    blueprint: {
      intro:
        'USMLE content is organized by the official USMLE Content Outline, which structures knowledge across organ systems alongside physician tasks and competencies, with per-step test specifications setting the emphasis for each examination.',
      points: [
        'Coverage organized by the outline\u2019s organ systems and the tasks-and-competencies axis',
        'Per-step emphasis follows the published test specifications, not a merged general curriculum',
        'The blueprint acts as a curation instrument: content outside your exam\u2019s scope stays out',
      ],
      sources: [
        { label: 'USMLE Content Outline (official PDF)', url: 'https://www.usmle.org/sites/default/files/2022-01/USMLE_Content_Outline_0.pdf' },
        { label: 'usmle.org', url: 'https://www.usmle.org' },
      ],
    },
    extras: ['Steps 1, 2 CK, and 3 under one subscription, each in its own register'],
    faq: [
      {
        q: 'Do I need three subscriptions for the three steps?',
        a: 'No. One USMLE subscription covers Steps 1, 2 CK, and 3. Pick the step you are studying inside the product; each keeps its own register and difficulty.',
      },
      {
        q: 'Is the content written in NBME style?',
        a: 'Items are generated in the exam\u2019s voice per step: mechanism-first for Step 1, diagnosis-and-management for Step 2 CK, independent-practice management stems for Step 3. Style is part of each exam\u2019s configuration, not an afterthought.',
      },
    ],
  },
  mccqe: {
    status: 'live',
    name: 'MCCQE Part I',
    fullName: 'Medical Council of Canada Qualifying Examination',
    jurisdiction: 'Canada',
    regulator: 'Medical Council of Canada',
    tagline: 'Canadian medicine, in English or French.',
    price: 'CA$419 / year',
    languages: ['English', 'French'],
    overview: [
      'The MCCQE Part I is Canada\u2019s qualifying examination, and Prep treats it as Canadian: Canadian guidelines, Canadian framing, and question content available in English or French, chosen per study block.',
      'Content is retrieved from the corpus slice scoped to the MCC\u2019s specification, so what you drill is what the Canadian exam holds you accountable for.',
    ],
    registers: [
      { name: 'English content', text: 'The full question, case, and explanation pipeline in English.' },
      { name: 'French content', text: 'The same certified corpus delivered as French question content: pick the language when you start a block.' },
    ],
    blueprint: {
      intro:
        'The MCC publishes a two-axis blueprint for the MCCQE Part I: Dimensions of Care, covering the spectrum of medical care, and Physician Activities, reflecting scope of practice and behaviours. Each axis has four weighted domains, and Prep\u2019s coverage is weighted to that published grid.',
      points: [
        'Dimensions of Care: from health promotion and prevention through acute and chronic care and psychosocial aspects',
        'Physician Activities: from assessment and diagnosis through management, communication, and professional behaviours',
        'Weighting follows the MCC\u2019s published content weightings for the exam',
      ],
      sources: [{ label: 'Medical Council of Canada: MCCQE Part I', url: 'https://mcc.ca' }],
    },
    extras: ['English and French question content from the same certified corpus'],
    faq: [
      {
        q: 'Is the French content a translation of the English bank?',
        a: 'French is a delivery language of the same certified, jurisdiction-scoped corpus. You choose the content language when starting a practice or case block; the provenance chain behind each item is the same.',
      },
      {
        q: 'Is this Canadian medicine or American medicine relabeled?',
        a: 'Canadian. Jurisdiction scoping is structural: the MCCQE slice of the corpus is retrieved separately from the USMLE slice, so Canadian guidelines and thresholds are not bleed-through from another country\u2019s exam.',
      },
    ],
  },
  ukmla: {
    status: 'live',
    name: 'UKMLA AKT',
    fullName: 'UK Medical Licensing Assessment, Applied Knowledge Test',
    jurisdiction: 'United Kingdom',
    regulator: 'General Medical Council',
    tagline: 'UK drug names, UK reference ranges, UK thresholds.',
    price: '\u00a3229 / year',
    languages: ['English'],
    overview: [
      'The UKMLA AKT tests safe practice at foundation level against the GMC\u2019s MLA content map. Prep\u2019s UKMLA content lives in its own lane: British drug names, UK reference ranges, and UK referral thresholds, never blended with another jurisdiction\u2019s medicine.',
      'Every generated item traces to a certified source and passes the same integrity gates as every other exam on the platform.',
    ],
    registers: [
      { name: 'The AKT register', text: 'Single-best-answer clinical vignettes at foundation-doctor standard: recognize, reason, and choose the safe next step under UK guidance.' },
    ],
    blueprint: {
      intro:
        'The GMC\u2019s MLA content map organizes what a new doctor must manage: areas of clinical practice, patient presentations and conditions, and overarching themes on the safe practice of medicine. The GMC is explicit that it is a coverage map rather than a fixed percentage blueprint, so breadth is the discipline, and Prep\u2019s coverage tracking is built for exactly that.',
      points: [
        'Coverage across the content map\u2019s areas of clinical practice, presentations, and conditions',
        'The safe-practice themes, prescribing, ethics and law, and uncertainty, treated as first-class content',
        'A coverage map means sampling everything: Prep\u2019s progress view shows the areas you have not touched yet',
      ],
      sources: [{ label: 'General Medical Council: MLA content map', url: 'https://www.gmc-uk.org' }],
    },
    extras: ['Built against the MLA content map\u2019s coverage-first structure'],
    faq: [
      {
        q: 'Does this cover PLAB?',
        a: 'The AKT and PLAB 1 are aligned to the same GMC content map, so AKT preparation is preparation against that shared standard. The clinical-skills component (CPSA / PLAB 2) is a separate, practical examination and is not part of this product today; skills exams are listed as coming, not live.',
      },
      {
        q: 'How current is the guidance behind the questions?',
        a: 'Items generate from certified reviews in the UKMLA-scoped corpus and are continuously re-audited. When the corpus does not support a defensible answer, the item is withheld rather than shipped.',
      },
    ],
  },
  amc: {
    status: 'live',
    name: 'AMC CAT MCQ',
    fullName: 'Australian Medical Council Computer Adaptive Test',
    jurisdiction: 'Australia',
    regulator: 'Australian Medical Council',
    tagline: 'Community-practice reasoning, weighted the way the AMC weights it.',
    price: 'AU$429 / year',
    languages: ['English'],
    overview: [
      'The AMC CAT MCQ tests medicine as it presents in Australian community practice. Prep\u2019s AMC content is weighted to the AMC\u2019s published blueprint, which distributes the exam across patient groups rather than a discipline list.',
      'Australian guidelines and referral norms stay in the Australian lane: jurisdiction scoping is structural, not a label on generic content.',
    ],
    registers: [
      { name: 'The AMC register', text: 'Community-practice reasoning: presentation-first vignettes with the management thresholds Australian practice expects.' },
    ],
    blueprint: {
      intro:
        'The AMC publishes the blueprint for the CAT MCQ weighted across patient groups, reflecting the population a starting doctor in Australia actually sees. Prep\u2019s coverage and its study plan weighting follow that published distribution.',
      points: [
        'Weighting by the AMC\u2019s published patient-group distribution',
        'Community-practice framing rather than tertiary-centre framing',
        'The study plan\u2019s coverage contract shows how each area was weighted, and why',
      ],
      sources: [{ label: 'Australian Medical Council', url: 'https://www.amc.org.au' }],
    },
    extras: ['Weighted to the AMC\u2019s published patient-group blueprint'],
    faq: [
      {
        q: 'The real exam is adaptive. Is the practice adaptive too?',
        a: 'The optional calibration quiz in the study plan is adaptive: it tunes item difficulty to your running performance per concept, then weights your plan with what it learns. Practice blocks and mocks are built to the exam\u2019s style and difficulty.',
      },
      {
        q: 'Is this Australian medicine?',
        a: 'Yes, structurally: the AMC slice of the corpus is retrieved separately, so Australian drug names, guidelines, and referral norms are what you practice against.',
      },
    ],
  },
  pres: {
    status: 'live',
    name: 'PRES Level 2',
    fullName: 'Pre-Registration Examination System, Level 2',
    jurisdiction: 'Ireland',
    regulator: 'Irish Medical Council',
    tagline: 'Irish registration, professionalism included.',
    price: '\u20ac269 / year',
    languages: ['English'],
    overview: [
      'PRES Level 2 is the Irish Medical Council\u2019s written examination for registration. Prep covers its clinical content and takes the professionalism and ethics dimension seriously, framed to the four domains of Good Medical Practice.',
      'As with every exam here, content generates from the certified, jurisdiction-scoped corpus and passes the integrity gates before you see it.',
    ],
    registers: [
      { name: 'The PRES register', text: 'Clinical single-best-answer items plus the professionalism, ethics, and communication content the examination expects.' },
    ],
    blueprint: {
      intro:
        'Coverage follows the Irish Medical Council\u2019s specification for PRES Level 2, including the professional-practice dimension framed to the four domains of Good Medical Practice, treated as examinable content rather than an afterthought.',
      points: [
        'Clinical coverage to the IMC\u2019s specification',
        'Professionalism and ethics framed to the four domains of Good Medical Practice',
        'Progress tracking that shows professional-practice areas alongside clinical systems',
      ],
      sources: [{ label: 'Irish Medical Council', url: 'https://www.medicalcouncil.ie' }],
    },
    extras: ['Professionalism and ethics treated as first-class examinable content'],
    faq: [
      {
        q: 'Does it cover the ethics and professionalism material?',
        a: 'Yes. Professional practice is part of the exam\u2019s specification, so it is part of the corpus scope, the question bank, and your progress tracking, not a separate pamphlet.',
      },
    ],
  },
  enarm: {
    status: 'development',
    name: 'ENARM',
    fullName: 'Examen Nacional de Aspirantes a Residencias M\u00e9dicas',
    jurisdiction: 'Mexico',
    regulator: 'CIFRHS, Secretar\u00eda de Salud',
    tagline: 'Spanish-language clinical cases, built for Mexican medicine.',
    price: null,
    languages: ['Spanish'],
    overview: [
      'ENARM is in active development. The Spanish-language clinical-case engine and the exam\u2019s official content weighting, sourced from the CIFRHS specification, are built; the exam goes live once the remaining source content is in place and a Mexican-peso price is set.',
      'ENARM on Prep is testlet-native: a shared patient vignette with linked decisions, graded together, with the case\u2019s level-of-management category (comprehensive management, joint management, or support and referral) revealed only after grading so it cannot hint at the answer.',
    ],
    registers: [
      { name: 'The ENARM register', text: 'Spanish-language referral-versus-treat decisions under Mexican clinical practice guidelines: the nivel de dominio axis is part of the case, not an add-on.' },
    ],
    blueprint: {
      intro:
        'Coverage weighting follows the official CIFRHS specification, including its clinical-impact weighting, with content scoped to Mexican clinical practice guidelines rather than another country\u2019s medicine translated.',
      points: [
        'Weighting sourced from the official CIFRHS specification',
        'Clinical cases built as coherent testlets, checked for plausibility before serving',
        'Mexican guidelines and referral norms, natively in Spanish',
      ],
      sources: [{ label: 'Secretar\u00eda de Salud (CIFRHS)', url: 'https://www.gob.mx/salud' }],
    },
    extras: ['Clinical-case testlets in Spanish, with the level-of-management axis built in'],
    faq: [
      {
        q: 'When does ENARM go live?',
        a: 'When the remaining source content is certified into the corpus and pricing is set. The engine, the Spanish case pipeline, and the CIFRHS weighting are already built. Ask us for the current status.',
      },
      {
        q: 'Will the interface be in Spanish too?',
        a: 'A machine-drafted Spanish interface translation exists across the platform and is gated until it clears human review. Verified, then released: exam content ships in Spanish; interface Spanish follows review.',
      },
    ],
  },
};

export const examOrder = ['usmle', 'mccqe', 'ukmla', 'amc', 'pres', 'enarm'];
